import type { ScopeFieldsContent } from '@/blocks/domain/types';
import type { RendererProps } from '../../domain/RendererContract';
import { resolveTokens } from '../../application/resolveTokens';
import { scopeFieldLabel, scopeFieldValue } from '../../application/scopeFieldLabels';
import { ACCENT_COLORS, BORDER_COLORS, TEXT_COLORS } from '@/common/presentation/constants/colors';

interface Props extends RendererProps<ScopeFieldsContent> {}

function ScopeFieldsRenderImpl({ content, context, style, knownPaths, slice }: Props) {
  const keys = Object.keys(context.scope ?? {}).sort();
  const start = slice?.startIndex ?? 0;
  const end = slice?.endIndex ?? keys.length;
  const visibleKeys = keys.slice(start, end);
  const showTitle = Boolean(content.title) && (!slice || slice.startIndex === 0 || slice.isContinuation);
  const minHeight = 40 + visibleKeys.length * 18;

  return (
    <section
      aria-label="block-scope-fields"
      style={{
        minHeight,
        paddingTop: style.paddingY,
        paddingBottom: style.paddingY,
        color: style.textColor,
        background: style.backgroundColor,
        fontSize: style.fontSize,
        fontWeight: style.fontWeight
      }}
    >
      {showTitle && content.title ? (
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '.08em',
            textTransform: 'uppercase',
            color: ACCENT_COLORS.DEEP_BLUE,
            marginBottom: 6
          }}
        >
          {resolveTokens(typeof content.title === 'string' ? content.title : '', context, knownPaths ? { knownPaths } : undefined)}
          {slice?.isContinuation ? ' (continued)' : ''}
        </div>
      ) : null}

      <dl style={{ margin: 0 }}>
        {visibleKeys.map((k) => (
          <div
            key={k}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 12,
              padding: '4px 0',
              borderBottom: `1px solid ${BORDER_COLORS.FAINT}`
            }}
          >
            <dt style={{ color: TEXT_COLORS.MUTED, margin: 0, fontWeight: 600 }}>{scopeFieldLabel(k)}</dt>
            <dd style={{ margin: 0, fontWeight: 600, textAlign: 'right' }}>{scopeFieldValue(context.scope[k])}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

export const renderer = {
  Component: ScopeFieldsRenderImpl,
  flowBehavior: 'splittable' as const
};

export type { ScopeFieldsContent };
